import { useEffect, useState } from 'react'
import mapboxgl, { Map } from 'mapbox-gl'
import * as turf from '@turf/turf'
import { MapPin, Spline } from 'lucide-react'
import { cn } from '@/lib/tailwind.utils'
import { BaseMap } from '@app/components/BaseMap/BaseMap'
import {
  BASE_MAP_LIGHT_WITH_HOUSE_NUMBERS,
  NEW_ZEALAND_LAT,
  NEW_ZEALAND_LGT,
  NEW_ZEALAND_ZOOM_LEVEL,
} from '@app/app-constants'
import { DocumentGeoreference } from '../documents.types'

const POINT_ZOOM_LEVEL = 14
const SOURCE_ID = 'documentGeoreferencePreviewSource'
const FILL_LAYER_ID = `${SOURCE_ID}-fill`
const LINE_LAYER_ID = `${SOURCE_ID}-line`

type GeoreferencePreviewMapProps = {
  georeference: DocumentGeoreference
  className?: string
}

// Non-interactive map thumbnail for a saved georeference — a marker for POINT, a
// filled/outlined shape fit to bounds for POLYGON.
export const GeoreferencePreviewMap = ({
  georeference,
  className,
}: GeoreferencePreviewMapProps) => {
  const [map, setMap] = useState<Map | null>(null)

  useEffect(() => {
    if (!map || georeference.geometry?.type !== 'Point') return
    const marker = new mapboxgl.Marker().setLngLat(georeference.geometry.coordinates).addTo(map)
    return () => {
      marker.remove()
    }
  }, [map, georeference])

  useEffect(() => {
    if (!map || georeference.geometry?.type !== 'Polygon') return
    const data: GeoJSON.Feature<GeoJSON.Polygon> = {
      type: 'Feature',
      properties: {},
      geometry: georeference.geometry,
    }

    map.addSource(SOURCE_ID, { type: 'geojson', data })
    map.addLayer({
      id: FILL_LAYER_ID,
      type: 'fill',
      source: SOURCE_ID,
      paint: { 'fill-color': '#0B2948', 'fill-opacity': 0.15 },
    })
    map.addLayer({
      id: LINE_LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#0B2948', 'line-width': 2 },
    })

    const [minLng, minLat, maxLng, maxLat] = turf.bbox(data)
    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 24, duration: 0 },
    )

    return () => {
      if (map.getLayer(FILL_LAYER_ID)) map.removeLayer(FILL_LAYER_ID)
      if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID)
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
    }
  }, [map, georeference])

  if (georeference.type === 'NONE' || !georeference.geometry) return null

  const { geometry } = georeference
  const isPoint = geometry.type === 'Point'
  const initialCenter: [number, number] =
    geometry.type === 'Point' ? geometry.coordinates : [NEW_ZEALAND_LGT, NEW_ZEALAND_LAT]
  const initialZoom = isPoint ? POINT_ZOOM_LEVEL : NEW_ZEALAND_ZOOM_LEVEL
  const label =
    georeference.searchLabel ??
    (geometry.type === 'Point' ? geometry.coordinates.map((v) => v.toFixed(5)).join(', ') : 'Area')

  return (
    <div className={cn('rounded-md border overflow-hidden', className)}>
      <div className="h-40">
        <BaseMap
          initialView={{ center: initialCenter, zoom: initialZoom }}
          style={BASE_MAP_LIGHT_WITH_HOUSE_NUMBERS}
          onMapReady={setMap}
          interactive
        />
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-t bg-slate-50">
        {isPoint ? (
          <MapPin className="h-4 w-4 text-primary shrink-0" />
        ) : (
          <Spline className="h-4 w-4 text-primary shrink-0" />
        )}
        <div className="min-w-0">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
            {isPoint ? 'Pinned location' : 'Drawn area'}
          </p>
          <p className="text-sm font-medium truncate">{label}</p>
        </div>
      </div>
    </div>
  )
}
